import { useMemo } from 'react';
import type { TaxonomyRecord } from '@/entities/taxonomy-dataset';
import type { CellDefect } from './completeness';

/** How many held-back rows fall to each reason, counted once each. */
export interface SaveGateReasons {
  incomplete: number;
  pattern: number;
  duplicate: number;
}

/** Which row keeps a contested name, and whether it looks like it does. */
export interface NameOwner {
  /** 1-based, matching the frozen row-number column. */
  row: number;
  /**
   * The owner keeps this name in the DATABASE while displaying a different one.
   *
   * True whenever the owner has been edited away from the name it still holds.
   * The marker has to say which, or the user goes to that row, reads something
   * else, and concludes the marker is broken.
   */
  retained: boolean;
}

export interface SaveGate {
  /** Collider row id -> the row that keeps the name. */
  collisions: Map<string, NameOwner>;
  /**
   * Records Save leaves alone. Always a subset of `dirtyIds`, and equal to it
   * whenever nothing can be sent — which is the case the button reads to disable
   * itself, so "strict" is exactly what it must not be.
   */
  heldBack: Set<string>;
  reasons: SaveGateReasons;
}

export interface SaveGateInput {
  rows: readonly TaxonomyRecord[];
  /** The PERSISTED rows, index-aligned with `rows` (`rows` is `records.map`). */
  records: readonly TaxonomyRecord[];
  dirtyIds: ReadonlySet<string>;
  /** Supplied by the caller so its cache keeps the identity `cost.test.tsx` bounds. */
  defectsFor: (row: TaxonomyRecord) => Map<string, CellDefect>;
}

/**
 * Which rows Save will leave behind, and why.
 *
 * A row is held back for one of two reasons: a cell defect, or a name another row
 * is keeping. Everything else saves — refusing the whole draft over one bad row
 * meant fifty correct edits became unsavable behind the fifty-first, and the only
 * exit was the navigation blocker, which discards them.
 *
 * DIRTY ONLY, and that is a safety property rather than an optimisation. Rows
 * saved incomplete before this gate existed must keep saving, or the gate locks
 * those users out of editing anything on a feature that already shipped. It also
 * bounds the cost to what was edited rather than to the 20 000-record cap.
 *
 * A local duplicate IS a real duplicate — the server's unique index refuses it —
 * so holding a row back here has no false positives. It has false NEGATIVES: that
 * index spans the whole schema, not this dataset, so a clash with a row in a
 * taxonomy the user is not looking at is invisible from here. The copy must not
 * promise otherwise, and the server's 409 stays the authority.
 *
 * ## Why a row's claim depends on whether it is held
 *
 * `applyRecordWrites` nulls a moving name only for the ids it was SENT, so the
 * database ends up holding exactly:
 *
 *     final(i) = sent(i) ? rows[i].assembledName : records[i].assembledName
 *
 * A held row is not sent, so it keeps the name it already had. Holding a row
 * therefore MOVES its claim from the name it now assembles to the name it
 * persists — and that can take a name away from a row that was about to be sent,
 * which then has to be held too, and so on. Whether a row is held decides which
 * name it claims, and the claims decide which rows are held, so this cannot be
 * read off a single grouping pass. Claiming BOTH names, as an earlier version
 * did, is wrong in both directions at once: it refused writes that were fine and
 * allowed writes the index rejects.
 *
 * It does not need iterating to a fixed point either. Persisted names are unique,
 * so a name has at most ONE row keeping it, and a row moving into that name has
 * exactly one thing that could take it away. The dependencies are a chain rather
 * than a web: hold a row, then hold whatever was targeting the name it just kept,
 * and repeat. Each row joins the queue once, which is what keeps this linear —
 * re-running the whole pass until the held set stops growing measured at 42
 * SECONDS per keystroke on a 20 000-row chain against 8ms for this one.
 */
export function computeSaveGate({ rows, records, dirtyIds, defectsFor }: SaveGateInput): SaveGate {
  /**
   * `frozen` is every row that will KEEP its persisted name, `held` only the
   * dirty ones Save is withholding. They differ by the clean rows: a row nobody
   * touched is never sent either, so it holds its name unconditionally and
   * permanently — but it is not something Save is "leaving behind", and
   * `heldBack` must stay a subset of the dirty ids.
   *
   * Seeding only the defective rows missed exactly this: a row edited into a name
   * that a CLEAN row persists was sent, and the index refused it, because only
   * one row ASSEMBLED that name so it never looked contested.
   */
  const frozen = new Set<number>();
  const held = new Set<number>();
  const pending: number[] = [];
  const collisions = new Map<string, NameOwner>();

  const freeze = (index: number) => {
    if (frozen.has(index)) {
      return;
    }
    frozen.add(index);
    pending.push(index);
  };

  rows.forEach((row, index) => {
    if (!dirtyIds.has(row.id)) {
      freeze(index);
    }
  });

  /*
   * Defective rows, and their reason, in one pass. A row can carry both defect
   * kinds; it is counted once, by this precedence, so the counts sum to exactly
   * the number of rows Save is leaving behind.
   */
  const defectiveIds = new Set<string>();
  let incomplete = 0;
  let pattern = 0;
  rows.forEach((row, index) => {
    if (!dirtyIds.has(row.id)) {
      return;
    }
    const kinds = new Set(defectsFor(row).values());
    if (kinds.size === 0) {
      return;
    }
    defectiveIds.add(row.id);
    held.add(index);
    freeze(index);
    if (kinds.has('required_empty')) {
      incomplete += 1;
    } else if (kinds.has('breaks_pattern')) {
      pattern += 1;
    }
  });

  /*
   * One index: name -> the rows that ASSEMBLE it.
   *
   * Pushed, not rebuilt. Rebuilding is O(k²) in the group size, and a fill that
   * sets one column across a dataset makes every row assemble the same name —
   * 20 000 rows in one group measured at ~500ms PER KEYSTROKE against ~0.5ms.
   *
   * Empty names are skipped: they are stored as NULL and the index ignores them,
   * so every blank row would otherwise look like a duplicate of every other.
   */
  const targeters = new Map<string, number[]>();
  rows.forEach((row, index) => {
    const name = row.assembledName;
    if (!name) {
      return;
    }
    const group = targeters.get(name);
    if (group) {
      group.push(index);
    } else {
      targeters.set(name, [index]);
    }
  });

  /**
   * Withholds `index`, blaming `ownerIndex`.
   *
   * The blame may be revised once: a row first held for losing live contention
   * can turn out to be losing to a row that never released the name at all, and
   * that is the more useful thing to point at. The QUEUE is pushed only on the
   * first hold, which is what bounds the loop below.
   */
  const hold = (index: number, ownerIndex: number) => {
    const row = rows[index];
    if (!dirtyIds.has(row.id)) {
      return;
    }
    held.add(index);
    freeze(index);
    collisions.set(row.id, {
      row: ownerIndex + 1,
      retained: rows[ownerIndex].assembledName !== row.assembledName,
    });
  };

  for (const [name, group] of targeters) {
    if (group.length < 2) {
      continue;
    }
    /*
     * The owner is the row in this group that already holds the name — a question
     * about its PERSISTED name, not about whether it is dirty. At most one row can
     * match: the index is unique per taxonomy.
     *
     * Failing that, the first row that is actually going to be SENT, preferring
     * one whose own persisted name somebody is waiting on — releasing that name
     * frees a row further down the chain, where releasing an unwanted one frees
     * nobody. Taking the first positional row instead handed the name to a row
     * already held for a defect, whose assembled name never reaches the database
     * at all, withholding a rival that had nothing to collide with.
     *
     * If every claimant is already frozen, nobody is claiming this name and there
     * is nothing to say about it — `sendable.at(0)` rather than `sendable[0]`
     * because that case is reachable (two defective rows assembling one name that
     * neither of them holds) and indexing would type as `number` and lie.
     *
     * The preference is ONE HOP: it asks whether somebody wants this row's name,
     * not how long the chain behind them is. Where both rivals qualify the choice
     * falls back to position, and the loser's whole chain is then held — so a
     * grid can hold far more rows than it strictly had to. Always in the safe
     * direction (a held row is visible and one edit away from saving), never a
     * violation, and a shortest-chain rule would cost a traversal per group on a
     * path that runs every keystroke.
     */
    let ownerIndex = group.find((index) => records[index]?.assembledName === name);
    if (ownerIndex === undefined) {
      const sendable = group.filter((index) => !frozen.has(index));
      ownerIndex =
        sendable.find((index) => {
          const persisted = records[index]?.assembledName;
          return Boolean(persisted) && targeters.has(persisted as string);
        }) ?? sendable.at(0);
    }
    if (ownerIndex === undefined) {
      continue;
    }
    for (const index of group) {
      if (index !== ownerIndex) {
        hold(index, ownerIndex);
      }
    }
  }

  // A frozen row keeps the name it persists, so whoever was moving into that name
  // has to be held too. `frozen` guards the push, so each row enqueues once.
  while (pending.length > 0) {
    const index = pending.pop() as number;
    const persisted = records[index]?.assembledName;
    if (!persisted) {
      continue;
    }
    const claimants = targeters.get(persisted);
    if (!claimants) {
      continue;
    }
    for (const other of claimants) {
      if (other !== index) {
        hold(other, index);
      }
    }
  }

  const heldBack = new Set<string>();
  for (const index of held) {
    heldBack.add(rows[index].id);
  }

  let duplicate = 0;
  for (const id of collisions.keys()) {
    if (!defectiveIds.has(id)) {
      duplicate += 1;
    }
  }

  return { collisions, heldBack, reasons: { incomplete, pattern, duplicate } };
}

/** `computeSaveGate`, memoised on the values it reads. */
export function useSaveGate(input: SaveGateInput): SaveGate {
  const { rows, records, dirtyIds, defectsFor } = input;
  return useMemo(
    () => computeSaveGate({ rows, records, dirtyIds, defectsFor }),
    [rows, records, dirtyIds, defectsFor],
  );
}
